import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { CollapseMenu } from 'components/UIElements'
import { pluralize } from 'utils/misc'
import { convertMinToDays } from 'utils/time'
import { convertAdvancedOptions } from 'utils/hoc/AdvancedOptionsHOC'
import {
  DEPLOYMENT_KEYS,
  TRIGGER_TYPE_MAP,
  INSTRUMENT_DISPLAY_LOCATION_OPTIONS,
  INSTRUMENT_TYPE_MAP,
  INSTRUMENT_TYPES_WITH_SCHEDULE_PLATFORM,
  SCHEDULE_PLATFORM_TYPE,
  coerceSchedulePlatformType,
  REPEAT_SUBTITLE_TEXT,
} from 'utils/constants'
import STRINGS from 'utils/strings'
import ExpireContent from './ExpireContent/ExpireContent'
import ParticipantManagement from './ParticipantManagement'
import RecurrenceContent from './RecurrenceContent'
import StartContent from './StartContent/StartContent'
import TimedCompletionContent from './TimedCompletionContent'
import TriggerManagement from './TriggerManagement'
import SaveReviewContent from './SaveReviewContent/SaveReviewContent'
import { REPEAT_TIME_UNIT_OPTION, UNIFICATION_REPEAT_TIME_UNIT_OPTION } from '../constants/schedulerConstants'
import VisibilityContainer from '../containers/VisibilityContainer'
import { ARTICLE_TYPE } from '../../../../../utils/instrumentConstants'
import useFoodDiaryFeature from 'utils/hooks/useFoodDiaryFeature'
import useInstrumentUnificationFeature from 'utils/hooks/useInstrumentUnificationFeature'
import { errorsMatchAnyKey } from 'utils/instrumentValidation'
import useSchedulingEngineFeature from 'utils/hooks/useSchedulingEngineFeature'

const ERROR_LISTS = {
  recurring: ['schedule.recurring'],
  visibility: ['schedule.recurring.visible_days', 'visibilityRanges'],
  participant: [
    'schedule.cohort.filter.max_age',
    'schedule.cohort.filter.min_age',
    'schedule.cohort.filter.site_ids',
    'schedule.cohort.filter.include',
    'schedule.cohort.include',
    'schedule.platform_type',
  ],
  trigger: ['conditions'],
}

export const getInstrumentDropdownOptions = (instrumentsList, instrumentId) => {
  const instrumentOptions = (instrumentsList || []).map(instrumentArr => {
    const indexOfName = instrumentArr.findIndex(arr => arr.key === 'name')

    return {
      key: instrumentArr.id,
      text: `${instrumentArr[indexOfName].value} (${instrumentArr.id})`,
      prev_instrument_id: instrumentArr[indexOfName].prev_instrument_id,
      instrumentTriggerAncestors: instrumentArr.instrumentTriggerAncestors,
      type: instrumentArr.payload.type,
    }
  })

  return instrumentOptions.filter(option => {
    const { instrumentTriggerAncestors } = option
    if (instrumentTriggerAncestors) {
      /**
       * If the instrument's trigger ancestors has the current instrument being scheduled, the instrument
       * cannot be selected. Trigger ancestors are which are all the previous generations
       * of instruments that trigger instruments prior to it in the chain.
       */
      return !option.instrumentTriggerAncestors.has(instrumentId) && option.key !== instrumentId
    }
    return option.key !== instrumentId && option.prev_instrument_id !== instrumentId
  })
}

const DeploymentOptions = props => {
  const {
    addDiaryNotificationTrigger,
    addInstrumentTrigger,
    advancedOptions,
    ageDisabled,
    changeTriggerType,
    checkedCohorts,
    checkedSites,
    clearPrevalidatedError,
    deleteInstrumentTrigger,
    disabled,
    disableTrackCreation,
    errors,
    fetchVisitTemplates,
    hasClinro,
    hasForcedPriorityEnabled,
    initializeCheckedSites,
    instrument,
    instrumentForm,
    instruments,
    instrumentTriggers,
    isArmsSupported,
    isDiary,
    isClinro,
    isNewInstArch,
    isSurvey,
    isVisitCycleSupported,
    leafSites,
    noTracksFeature,
    schedule,
    scoreOptions,
    setAdvancedOptions,
    setShowAdvancedOption,
    showAdvancedOption,
    sitesList,
    study,
    studyID,
    studyVisitSchedule,
    toggleSite,
    triggersLoading,
    updateInstrumentAction,
    updateInstrumentTrigger,
    updatePrevalidatedErrors,
    visitTemplates,
    visibility,
    userId,
    isReadOnlyState,
    monthlyRepeatEnabled,
  } = props
  const { config } = study
  const studyHasVirtualVisits = !!config?.virtual_visits

  const { hasFoodDiary } = useFoodDiaryFeature({ study, userId })
  const { hasInstrumentUnification } = useInstrumentUnificationFeature({ study, userId })
  const { hasSchedulingEngine } = useSchedulingEngineFeature({ study })

  const { chain_deployment_info = {}, cohort, deploy, expire, recurring } = schedule

  const isRelative = DEPLOYMENT_KEYS.firstLogin in deploy
  const isRelativeToInstrumentSentDate = DEPLOYMENT_KEYS.relativeToSendIdeal in expire
  const isInstrumentToInstrument = DEPLOYMENT_KEYS.chainDeployOffset in deploy
  const isDeployedByVisit = DEPLOYMENT_KEYS.chainDeployByVisit in chain_deployment_info
  const isVisitSummary = isDeployedByVisit && instrument?.metadata.article_type === ARTICLE_TYPE.visit_summary.key

  const isPersistentInstrument = recurring?.scheme === 'persistent'

  const isCalendarModeChecked = instrument?.metadata?.display_location?.includes(
    INSTRUMENT_DISPLAY_LOCATION_OPTIONS.calendar,
  )

  const isTaskOrArticle = instrument?.type === INSTRUMENT_TYPE_MAP.task || instrument?.type === INSTRUMENT_TYPE_MAP.article

  const otherInstruments = getInstrumentDropdownOptions(instruments, instrument.id)
  const noOtherInstrumentForTrigger = isInstrumentToInstrument && otherInstruments.length === 0

  // Diary triggers (for push notifications) will be hidden to the admin
  const visibleTriggers = instrumentTriggers?.conditions?.filter(condition => {
    const { triggers } = condition
    const triggerType = triggers ? triggers[0].resource_function_name : ''
    return triggerType !== TRIGGER_TYPE_MAP.newActivityNotification
  })

  const instrumentType = (instrument?.type || '').toUpperCase()
  const instrumentIsClinro = isClinro || instrumentType === INSTRUMENT_TYPE_MAP.clinro
  const showSchedulePlatformTargets =
    !instrumentIsClinro && INSTRUMENT_TYPES_WITH_SCHEDULE_PLATFORM.includes(instrumentType)

  const cohortSubtitleContent = _getCohortSubtitles(
    cohort,
    checkedCohorts,
    showAdvancedOption ? advancedOptions : null,
    showSchedulePlatformTargets ? schedule?.platform_type : null,
  )

  useEffect(() => {
    if (noOtherInstrumentForTrigger) {
      updatePrevalidatedErrors({ inInstrumentLoop: '' })
    } else {
      clearPrevalidatedError('inInstrumentLoop')
    }
  }, [noOtherInstrumentForTrigger])

  const options = hasInstrumentUnification ? UNIFICATION_REPEAT_TIME_UNIT_OPTION : REPEAT_TIME_UNIT_OPTION
  const filteredOptions = options.filter(option => {
    if (option.key === 'n-months' && !monthlyRepeatEnabled) {
      return false
    }
    return true
  })

  const getRepeatSubtitle = () => {
    const hasVisitConfirmationNeverExpire = isDeployedByVisit && !!expire?.expire_on_visit_confirmation

    if (!recurring) {
      // When "Never expire" + "Unless visit confirmation applies" is selected for
      // visit-triggered instruments, the repeat behaviour is effectively locked
      // by visit confirmation, not by a recurring schedule. In this case we
      // should not display "Doesn't repeat", which is misleading.
      if (hasVisitConfirmationNeverExpire) {
        return `${REPEAT_SUBTITLE_TEXT.repeatAfterEvery} 1 day`
      }

      return REPEAT_SUBTITLE_TEXT.doesntRepeat
    }

    if (recurring.scheme === 'n-days') {
      return `${REPEAT_SUBTITLE_TEXT.repeatAfterEvery} ${pluralize(recurring.interval, 'day', 'days')}`
    }

    if (recurring.scheme === 'n-months') {
      return `${REPEAT_SUBTITLE_TEXT.repeatAfterEvery} ${pluralize(recurring.interval, 'month', 'months')}`
    }

    return REPEAT_SUBTITLE_TEXT.repeatImmediately
  }

  return (
    <div className={`${disabled ? 'disabled' : ''}`}>
      <>
        <StartContent
          instrumentId={instrument.id}
          isInstrumentToInstrument={isInstrumentToInstrument}
          isDeployedByVisit={isDeployedByVisit}
          isRelative={isRelative}
          otherInstruments={otherInstruments}
          isVisitSummary={isVisitSummary}
          {...props}
          disabled={noOtherInstrumentForTrigger || isReadOnlyState || disabled}
        />
        <ExpireContent
          disabled={noOtherInstrumentForTrigger || isReadOnlyState}
          isDiary={isDiary}
          isRelative={isRelative}
          isRelativeToInstrumentSentDate={isRelativeToInstrumentSentDate}
          isInstrumentToInstrument={isInstrumentToInstrument}
          isDeployedByVisit={isDeployedByVisit}
          isVisitSummary={isVisitSummary}
          instrument={instrument}
          hasInstrumentUnification={hasInstrumentUnification}
          chain_deployment_info={chain_deployment_info}
          studyID={studyID}
          fetchVisitTemplates={fetchVisitTemplates}
          visitTemplates={visitTemplates}
          isVisitCycleSupported={isVisitCycleSupported}
          isArmsSupported={isArmsSupported}
          studyVisitSchedule={studyVisitSchedule}
          errors={errors}
          clearPrevalidatedError={clearPrevalidatedError}
          updatePrevalidatedErrors={updatePrevalidatedErrors}
        />
        {hasFoodDiary && !isTaskOrArticle && !hasSchedulingEngine && !instrumentIsClinro ? (
          <SaveReviewContent {...props} />
        ) : null}
      </>
      <p>Advanced Options</p>
      {hasInstrumentUnification ? (
        <>
          {!isVisitSummary && (
            <CollapseMenu
              hasError={errorsMatchAnyKey(errors, ERROR_LISTS.recurring)}
              useErrorIcon
              className='repeat-settings'
              titleContent={STRINGS.repeat}
              subtitleContent={
                recurring && filteredOptions.some(opt => opt.key === recurring.scheme)
                  ? `Repeat after every ${pluralize(
                    recurring.interval,
                    recurring.interval_type.substring(0, recurring.interval_type.length - 1),
                    recurring.interval_type,
                  )}`
                  : getRepeatSubtitle()
              }
              menuContent={
                <RecurrenceContent
                  hideImmediateRepeat={hasForcedPriorityEnabled}
                  isRelative={isRelative}
                  isDeployedByVisit={isDeployedByVisit}
                  hasInstrumentUnification={hasInstrumentUnification}
                  isReadOnlyState={isReadOnlyState}
                  monthlyRepeatEnabled={monthlyRepeatEnabled}
                />
              }
            />
          )}
          <CollapseMenu
            className='visibility-settings'
            hasError={errorsMatchAnyKey(props.errors, ERROR_LISTS.visibility)}
            menuContent={<VisibilityContainer isReadOnlyState={isReadOnlyState} />}
            subtitleContent={_getVisibilitySubtitle({ visibility, recurring })}
            titleContent={STRINGS.visibility}
            useErrorIcon
          />
        </>
      ) : (
        <>
          {!isVisitSummary && (
            <CollapseMenu
              hasError={errorsMatchAnyKey(errors, ERROR_LISTS.recurring)}
              useErrorIcon
              className='repeat-settings'
              titleContent={STRINGS.repeat}
              subtitleContent={getRepeatSubtitle()}
              menuContent={
                <RecurrenceContent
                  hideImmediateRepeat={hasForcedPriorityEnabled || isDiary}
                  isRelative={isRelative}
                  isDeployedByVisit={isDeployedByVisit}
                  hasInstrumentUnification={hasInstrumentUnification}
                  isReadOnlyState={isReadOnlyState}
                  monthlyRepeatEnabled={monthlyRepeatEnabled}
                />
              }
            />
          )}
          {!isDiary ? (
            <CollapseMenu
              className='visibility-settings'
              hasError={errorsMatchAnyKey(props.errors, ERROR_LISTS.visibility)}
              menuContent={<VisibilityContainer isReadOnlyState={isReadOnlyState} />}
              subtitleContent={_getVisibilitySubtitle({ visibility, recurring })}
              titleContent={STRINGS.visibility}
              useErrorIcon
            />
          ) : null}
        </>
      )}

      {(isDiary || hasInstrumentUnification) && !isPersistentInstrument && !instrumentIsClinro ? (
        <CollapseMenu
          className='timed-entry-window'
          menuContent={
            <TimedCompletionContent
              hasInstrumentUnification={hasInstrumentUnification}
              isDiary={isDiary}
              isCalendarModeChecked={isCalendarModeChecked}
              isReadOnlyState={isReadOnlyState}
              {...props}
            />
          }
          titleContent={STRINGS.timedCompletion}
          subtitleContent={_getTimedCompletionSubtitle(schedule, isDiary)}
        />
      ) : null}
      <CollapseMenu
        hasError={
          errorsMatchAnyKey(errors, ERROR_LISTS.participant) || typeof cohortSubtitleContent === 'object'
        }
        useErrorIcon
        className={`cohort-settings ${typeof cohortSubtitleContent === 'object' ? 'with-error' : ''}`}
        titleContent={STRINGS.sendTo}
        subtitleContent={
          typeof cohortSubtitleContent === 'string' ? cohortSubtitleContent : cohortSubtitleContent.subtitleContent
        }
        menuContent={
          <ParticipantManagement
            ageDisabled={ageDisabled}
            checkedCohorts={checkedCohorts}
            checkedSites={checkedSites}
            disableTrackCreation={disableTrackCreation}
            errors={errors}
            initializeCheckedSites={initializeCheckedSites}
            leafSites={leafSites}
            noTracksFeature={noTracksFeature}
            sitesList={sitesList}
            toggleSite={toggleSite}
            advancedOptions={advancedOptions}
            setAdvancedOptions={setAdvancedOptions}
            showAdvancedOption={showAdvancedOption}
            setShowAdvancedOption={setShowAdvancedOption}
            isReadOnlyState={isReadOnlyState}
            caregiverEnabled={!!study?.caregiver_enabled}
            showParticipantInstrumentPlatformTargets={showSchedulePlatformTargets}
          />
        }
      />
      <CollapseMenu
        hasError={errorsMatchAnyKey(errors, ERROR_LISTS.trigger)}
        useErrorIcon
        loading={triggersLoading}
        className='trigger-settings'
        titleContent={STRINGS.triggerAnotherItem}
        subtitleContent={`Trigger ${triggersLoading ? '--- items' : pluralize(visibleTriggers?.length, 'item', 'items')
          }`}
        menuContent={
          <TriggerManagement
            addDiaryNotificationTrigger={addDiaryNotificationTrigger}
            addInstrumentTrigger={addInstrumentTrigger}
            changeTriggerType={changeTriggerType}
            errors={errors}
            deleteInstrumentTrigger={deleteInstrumentTrigger}
            studyHasVirtualVisits={studyHasVirtualVisits}
            scoreOptions={scoreOptions}
            instrument={instrument}
            instrumentForm={instrumentForm}
            instrumentTriggers={instrumentTriggers}
            isDiary={isDiary}
            isSurvey={isSurvey}
            hasClinro={hasClinro}
            study={study}
            updateInstrumentTrigger={updateInstrumentTrigger}
            updateInstrumentAction={updateInstrumentAction}
            isNewInstArch={isNewInstArch}
            isReadOnlyState={isReadOnlyState}
          />
        }
      />
    </div>
  )
}

const _getVisibilitySubtitle = ({ visibility, recurring }) => {
  const visibleDaysText =
    recurring && recurring.interval !== recurring.visible_days ? `${recurring.visible_days} day(s)` : 'Every day '
  const visibilityText = visibility === 'all_day' ? ' & all day' : ' at specific times'
  return visibleDaysText + visibilityText
}

const _getCohortSubtitle = (cohort, subtitleList) => {
  const { type, filter } = cohort
  switch (type) {
    case 'age':
      const { min_age, max_age } = filter
      const minAge = min_age || min_age === 0 ? min_age : '--'
      const maxAge = max_age || max_age === 0 ? max_age : '--'
      subtitleList.push(`Participants between ${minAge} and ${maxAge} years of age`)
      break
    case 'study_group':
      subtitleList.push(`${pluralize(filter.include.length, 'track', 'tracks')}`)
      break
    case 'sites':
      subtitleList.push(`${pluralize(filter.site_ids.length, 'site', 'sites')}`)
      break
    case 'all_participants':
      subtitleList.push('Participants')
      break
    case 'all_caregivers':
      subtitleList.push('Caregivers')
      break
    case 'all_users':
      subtitleList.push('Participants & Caregivers')
      break
    default:
      break
  }
}

const _getPlatformSubtitle = platformType => {
  if (platformType == null) return null
  const coerced = coerceSchedulePlatformType(platformType)
  if (coerced === SCHEDULE_PLATFORM_TYPE.all) return 'Mobile, Web'
  if (coerced === SCHEDULE_PLATFORM_TYPE.mobile) return 'Mobile'
  return 'Web'
}

const _getCohortSubtitles = (cohort, checkedCohorts, advancedOptions = null, targetPlatforms = null) => {
  const subtitleList = []
  const convertedAdvancedOptions = convertAdvancedOptions(advancedOptions)
  if (convertedAdvancedOptions && convertedAdvancedOptions.length) {
    convertedAdvancedOptions.forEach(item => {
      _getCohortSubtitle(item, subtitleList)
    })
  } else {
    _getCohortSubtitle(cohort, subtitleList)
  }
  const platformSubtitle = _getPlatformSubtitle(targetPlatforms)
  if (platformSubtitle) subtitleList.push(platformSubtitle)
  if (subtitleList.length) return subtitleList.join(', ')
  if (Object.keys(cohort).length === 0) {
    return {
      subtitleContent: 'Must assign at least one',
    }
  }
  // Default fallback - check if it's one of the new audience types
  if (cohort.type === 'all_participants') {
    return 'Participants'
  }
  if (cohort.type === 'all_caregivers') {
    return 'Caregivers'
  }
  if (cohort.type === 'all_users') {
    return 'Participants & Caregivers'
  }
  return 'All participants'
}

const _getTimedCompletionSubtitle = (schedule, isDiary) => {
  const { submission_expiry, timeout_window = {} } = schedule
  const { submission = {} } = timeout_window
  const { value } = submission
  if (!submission_expiry || !submission)
    return isDiary ? STRINGS.timedEntryPtpsAlwaysAllowedSubDiary : STRINGS.timedEntryPtpsAlwaysAllowedSub
  const { expires_in } = submission_expiry
  return `Allow ${pluralize(convertMinToDays(value, false) || expires_in, STRINGS.day, STRINGS.days)} ${isDiary ? STRINGS.timedEntryPtpsAlwaysAllowedSubSuffixDiary : STRINGS.timedEntryPtpsAlwaysAllowedSubSuffix
    }`
}

DeploymentOptions.propTypes = {
  addDiaryNotificationTrigger: PropTypes.func,
  addInstrumentTrigger: PropTypes.func,
  ageDisabled: PropTypes.bool,
  clearPrevalidatedError: PropTypes.func,
  changeTriggerType: PropTypes.func,
  checkedSites: PropTypes.objectOf(PropTypes.string),
  deleteInstrumentTrigger: PropTypes.func,
  initializeCheckedSites: PropTypes.func,
  instrument: PropTypes.shape({ id: PropTypes.string }),
  instrumentForm: PropTypes.shape({ id: PropTypes.string }),
  instruments: PropTypes.arrayOf(PropTypes.array),
  instrumentTriggers: PropTypes.objectOf(PropTypes.array),
  isNewInstArch: PropTypes.bool,
  isDiary: PropTypes.bool,
  isSurvey: PropTypes.bool,
  hasClinro: PropTypes.bool,
  leafSites: PropTypes.arrayOf(PropTypes.number),
  noTracksFeature: PropTypes.bool,
  setIsInInstrumentLoopState: PropTypes.func,
  sitesList: PropTypes.arrayOf(PropTypes.array),
  study: PropTypes.shape({
    config: PropTypes.shape({
      clinro: PropTypes.object,
    }),
  }),
  toggleSite: PropTypes.func,
  triggersLoading: PropTypes.bool,
  updateInstrumentTrigger: PropTypes.func,
  updateInstrumentAction: PropTypes.func,
  updatePrevalidatedErrors: PropTypes.func,
  schedule: PropTypes.shape({
    chain_deployment_info: PropTypes.object,
    cohort: PropTypes.object,
    deploy: PropTypes.object,
    expire: PropTypes.object,
    recurring: PropTypes.object,
  }),
  scoreOptions: PropTypes.array,
  updateTriggerError: PropTypes.func,
  advancedOptions: PropTypes.object,
  setAdvancedOptions: PropTypes.func,
  showAdvancedOption: PropTypes.bool,
  setShowAdvancedOption: PropTypes.func,
}

export default DeploymentOptions